import { t } from '@lingui/core/macro'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { isHTTPError } from 'ky'

import { antdStaticApp } from '@/tshared/web/components/AntdStaticApp.tsx'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** When true, skip the global MutationCache error toast — caller handles it. */
      skipGlobalError?: boolean
    }
    queryMeta: {
      /** When true, skip the global QueryCache error toast — caller handles it. */
      skipGlobalError?: boolean
      persist?: boolean
    }
  }
}

type ApiErrorData = {
  showMessage?: boolean
  message?: unknown
}

function getApiErrorData(data: unknown): ApiErrorData | null {
  return data && typeof data === 'object' ? (data as ApiErrorData) : null
}

function getApiErrorMessage(data: ApiErrorData): string {
  return typeof data.message === 'string' && data.message.length > 0
    ? data.message
    : t`An error occurred`
}

function handleApiError(error: unknown) {
  if (!isHTTPError(error)) return

  if (error.response.status === 403) {
    antdStaticApp.message.error(t`You are not authorized to access this resource`)
    return
  }

  const data = getApiErrorData(error.data)
  if (data?.showMessage) {
    antdStaticApp.message.error(getApiErrorMessage(data))
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipGlobalError) return
      handleApiError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.skipGlobalError) return
      handleApiError(error)
    },
  }),
  defaultOptions: {
    queries: {
      // TanStack Query default: 3 retries with exponential backoff.
      // Override per-query when retries are harmful (e.g. expected 404).
      retry: 3,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // TanStack Query default: do not retry writes globally (avoids duplicate side effects).
      // Opt in per-mutation for idempotent / safe-to-retry calls (e.g. hangup, status sync).
      retry: 0,
    },
  },
})

export default queryClient
